import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CategoryToggle } from '@/components/consent/CategoryToggle'
import { LegalHeader } from '@/legal/components/LegalHeader'
import { useConsentStore } from '@/consent/consentStore'
import { DEFAULT_CHOICES } from '@/consent/types'
import type { ConsentCategory } from '@/consent/types'

/**
 * CookieSettingsPage — /account/privacy/cookies.
 *
 * CNIL-compliant cookie management UI:
 * - 4 toggles (essential disabled by invariant);
 * - "Сохранить" persists current selection;
 * - "Принять все" / "Отклонить все" mirror banner actions for symmetry;
 * - All actions go through useConsentStore so backend audit log + localStorage stay in sync.
 */
export function CookieSettingsPage() {
  const { t } = useTranslation('consent')
  const choices = useConsentStore((s) => s.choices)
  const updateChoices = useConsentStore((s) => s.updateChoices)
  const saveCurrentChoices = useConsentStore((s) => s.saveCurrentChoices)
  const acceptAll = useConsentStore((s) => s.acceptAll)
  const rejectAll = useConsentStore((s) => s.rejectAll)

  const current = choices ?? DEFAULT_CHOICES

  const handleSave = async () => {
    await saveCurrentChoices('settings_page')
    toast.success(t('settings.saved'))
  }

  const handleToggle = (category: ConsentCategory, checked: boolean) => {
    if (category === 'essential') return // invariant
    updateChoices({ [category]: checked })
  }

  const categories: ConsentCategory[] = ['essential', 'functional', 'analytics', 'marketing']

  return (
    <div className="min-h-screen bg-surface-canvas">
      <LegalHeader
        title={t('settings.title')}
        breadcrumbs={[
          { label: 'Аккаунт', href: '/' },
          { label: 'Приватность' },
          { label: t('settings.title') },
        ]}
      />
      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-3xl font-semibold text-text-primary font-display">
          {t('settings.title')}
        </h1>

        <div className="mt-8 space-y-3">
          {categories.map((cat) => (
            <CategoryToggle
              key={cat}
              category={cat}
              checked={current[cat]}
              disabled={cat === 'essential'}
              onChange={(checked) => handleToggle(cat, checked)}
            />
          ))}
        </div>

        <div className="sticky bottom-0 mt-8 -mx-4 border-t border-border bg-surface-raised px-4 py-4">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                void handleSave()
              }}
              className="h-11 rounded-lg bg-action-primary-bg px-5 text-sm font-medium text-text-on-brand transition hover:bg-action-primary-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
            >
              {t('settings.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                void acceptAll('settings_page')
              }}
              className="h-11 rounded-lg border border-action-secondary-border bg-action-secondary-bg px-5 text-sm font-medium text-text-primary transition hover:bg-action-secondary-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
            >
              {t('banner.acceptAll')}
            </button>
            <button
              type="button"
              onClick={() => {
                void rejectAll('settings_page')
              }}
              className="h-11 rounded-lg border border-action-secondary-border bg-action-secondary-bg px-5 text-sm font-medium text-text-primary transition hover:bg-action-secondary-bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus"
            >
              {t('banner.rejectAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
